import { configureStore } from '@reduxjs/toolkit';
import {
    persistStore,
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
    PersistedState,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web and AsyncStorage for react-native
import constants from './constants';
import reducers from './reducers';
import { dashboardExportTransform } from './dashboard/dashboardSlice';
import { withoutSecurityDashboard } from './utils/persistMigrate';

const { ENV } = constants;

/**
 * ## configureStore
 * @param {Object} the state
 */
const persistConfig = {
    key: 'vfm-root',
    storage,
    // Transient request/booking working state is re-fetched from the API on
    // navigation and carries personal data — including guest requester name/email
    // on one-off bookings. Keep these slices out of localStorage so guest (and
    // requester) PII never lingers on the device after the session. The
    // security dashboard is the same: visitor names, re-fetched on load, and a
    // saved copy from an older release can lack fields the page now reads.
    blacklist: ['visitRequestForm', 'visitRequestReview', 'bookingLinks', 'securityDashboard'],
    transforms: [dashboardExportTransform],
    // The blacklist only stops future saves; drop a copy saved before it.
    migrate: (state: PersistedState) => Promise.resolve(withoutSecurityDashboard(state)),
};

const persistedReducer = persistReducer(persistConfig, reducers);

export const store = configureStore({
    reducer: persistedReducer,
    devTools: ENV !== 'production',
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
        serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
    }),
});

export const persistor = persistStore(store);
